import React, { useEffect, useRef, useState, useCallback } from "react";
import logger from "@/lib/logger";
import { requestMedia } from "@/lib/mediaPermissions";
import PermissionDeniedDialog from "@/components/PermissionDeniedDialog";
import MusicPicker from "@/components/MusicPicker";
import { Microphone, StopCircle, X, PaperPlaneRight, SlidersHorizontal, MusicNotes } from "@phosphor-icons/react";
import { VOICE_FILTERS, applyFilterToArrayBuffer } from "@/lib/voiceFilters";
import { AMBIENT_BEDS } from "@/lib/ambientBeds";
import { api } from "@/lib/api";

function fmt(s) {
  const m = Math.floor(s / 60), r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

export default function VoiceRecorder({ open, onClose, onComplete, title = "Record a voice clip", subtitle = null }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);
  // rawBlob is the unfiltered recording. processedBlob is what we send.
  const [rawBlob, setRawBlob] = useState(null);
  const [processedBlob, setProcessedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [preset, setPreset] = useState("none");
  const [showFilters, setShowFilters] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [denial, setDenial] = useState(null);
  const [level, setLevel] = useState(0); // 0..1 RMS for the live mic meter
  const [music, setMusic] = useState(null); // { url, title, artist, artwork }
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [pickingBedId, setPickingBedId] = useState(null);

  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const musicAudioRef = useRef(null);
  const musicSourceRef = useRef(null);
  const musicGainRef = useRef(null);
  const rawArrayBufRef = useRef(null);

  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const startingRef = useRef(false);
  const sendingRef = useRef(false);
  const sessionRef = useRef(0);
  const filterRef = useRef(0);
  const previewRef = useRef(null);

  const replacePreview = useCallback((blob) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = blob ? URL.createObjectURL(blob) : null;
    setPreviewUrl(previewRef.current);
  }, []);

  const releaseMedia = useCallback(() => {
    const mr = mediaRecRef.current;
    mediaRecRef.current = null;
    if (mr) {
      mr.onstop = null;
      mr.ondataavailable = null;
      mr.onerror = null;
      try { if (mr.state !== "inactive") mr.stop(); } catch (_) {}
      mr.stream?.getTracks().forEach((t) => t.stop());
    }
    clearInterval(timerRef.current);
    cancelAnimationFrame(rafRef.current);
    timerRef.current = null;
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { musicSourceRef.current?.stop(); } catch (_) {}
    musicSourceRef.current = null;
    musicAudioRef.current?.pause();
    musicAudioRef.current = null;
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});
  }, []);

  // Invalidate asynchronous work on close AND unmount.
  useEffect(() => {
    setRecording(false); setSeconds(0); setRawBlob(null); setProcessedBlob(null);
    setPreviewUrl(null); setError(null); setPreset("none"); setShowFilters(true);
    setProcessing(false); setLevel(0); setMusic(null); setDenial(null);
    setShowMusicPicker(false); setPickingBedId(null);
    setStarting(false); setSending(false);
    return () => {
      sessionRef.current += 1;
      filterRef.current += 1;
      startingRef.current = false;
      sendingRef.current = false;
      releaseMedia();
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
      rawArrayBufRef.current = null;
    };
  }, [open, releaseMedia]);

  const stopLevelMeter = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setLevel(0);
  }, []);

  const startLevelMeter = useCallback((analyser) => {
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      // RMS in 0..1
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      // Soft compress so quiet voices still register but it doesn't peg at 1
      setLevel(Math.min(1, rms * 2.4));
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const start = async () => {
    if (!open || startingRef.current || sendingRef.current || recording) return;
    startingRef.current = true;
    setStarting(true);
    const session = ++sessionRef.current;
    filterRef.current += 1;
    releaseMedia();
    setProcessing(false);
    setError(null); setRawBlob(null); setProcessedBlob(null); replacePreview(null);
    rawArrayBufRef.current = null;
    setPreset("none");
    setDenial(null);
    try {
    const result = await requestMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 48000,
      },
      video: false,
    });
    if (session !== sessionRef.current) {
      if (result.ok) result.stream.getTracks().forEach((t) => t.stop());
      return;
    }
    if (!result.ok) { setDenial(result); return; }
    const stream = result.stream;
      streamRef.current = stream;

      // Web Audio graph:
      //   mic ──► analyser ─────────────────────┐
      //                                          ▼
      //                                    MediaStreamDestination ─► MediaRecorder
      //                                          ▲
      //   music (optional) ──► gain (0.35) ──────┘
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      ctxRef.current = ctx;
      await ctx.resume();
      if (session !== sessionRef.current) return;
      const micSource = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      micSource.connect(analyser);

      const dest = ctx.createMediaStreamDestination();
      // Mic always feeds the recording at unity.
      const micGain = ctx.createGain(); micGain.gain.value = 1.0;
      micSource.connect(micGain); micGain.connect(dest);

      // Optionally wire backing music into the same destination so the file
      // contains voice+music baked together.
      //
      // CRITICAL: iTunes preview URLs are CORS-restricted. If we use
      // `createMediaElementSource(<audio src=cross-origin>)` the AudioContext
      // outputs SILENCE for that node (browser security) — so the speakers
      // played the music but the MediaRecorder captured zero. We work around
      // it by fetching the audio bytes through our own backend proxy (which
      // adds `Access-Control-Allow-Origin: *`), decoding to an AudioBuffer,
      // and playing as an AudioBufferSourceNode — which has full sample
      // access regardless of origin.
      if (music?.url) {
        try {
          const proxied = `${process.env.REACT_APP_BACKEND_URL}/api/music/proxy?url=${encodeURIComponent(music.url)}`;
          const resp = await fetch(proxied);
          if (!resp.ok) throw new Error(`music fetch ${resp.status}`);
          if (session !== sessionRef.current) return;
          const buf = await resp.arrayBuffer();
          const audioBuf = await ctx.decodeAudioData(buf.slice(0));
          if (session !== sessionRef.current) return;
          const src = ctx.createBufferSource();
          src.buffer = audioBuf;
          src.loop = true;
          const mg = ctx.createGain(); mg.gain.value = 0.4;
          src.connect(mg); mg.connect(dest);
          src.start();
          musicSourceRef.current = src;
          musicGainRef.current = mg;
        } catch (err) {
          logger.warn("voice-recorder", "music wire-up failed", err);
        }
      }

      if (session !== sessionRef.current) return;
      startLevelMeter(analyser);

      const audioCandidates = [
        // PRIORITY: audio/mp4 / AAC is the only format every major browser
        // (Chrome, Safari, iOS Safari, Edge) can both record AND play. webm
        // was first here historically, but iOS Safari REFUSES to play webm,
        // so Chrome → iPhone voice DMs silently failed. By trying mp4 first we
        // cover the 95% case; Firefox (which can't record mp4) falls through
        // to webm/ogg.
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/aac",
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
      ];
      const mimeType = audioCandidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const mr = new MediaRecorder(dest.stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 192_000,
      });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        if (session !== sessionRef.current) return;
        const b = new Blob(chunksRef.current, { type: mr.mimeType || mimeType });
        releaseMedia();
        startingRef.current = false; setStarting(false);
        setRecording(false);
        if (!b.size) { setError("No audio was captured. Please record again."); return; }
        setRawBlob(b);
        setProcessedBlob(b);
        replacePreview(b);
        stopLevelMeter();
        try {
          const buf = await b.arrayBuffer();
          if (session === sessionRef.current) rawArrayBufRef.current = buf;
        } catch (err) {
          logger.warn("voice-recorder", "decode-prep failed", err);
        }
      };
      mr.onerror = () => {
        if (session !== sessionRef.current) return;
        releaseMedia(); setRecording(false); setLevel(0);
        setError("Recording stopped unexpectedly. Please record again.");
      };
      mediaRecRef.current = mr;
      mr.start(1000);
      setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      logger.warn("voice-recorder", "start failed", e);
      if (session === sessionRef.current) {
        releaseMedia(); setLevel(0);
        setError("Couldn't start recording. Please try again.");
      }
    } finally {
      if (session === sessionRef.current) { startingRef.current = false; setStarting(false); }

    }
  };

  const stop = () => {
    startingRef.current = true; setStarting(true);
    try { mediaRecRef.current?.stop(); } catch (err) { logger.warn("voice-recorder", "stop failed", err); }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const applyFilter = async (newPreset) => {
    const generation = ++filterRef.current;
    setPreset(newPreset);
    if (!rawBlob) return;
    if (newPreset === "none") {
      setProcessedBlob(rawBlob);
      replacePreview(rawBlob);
      return;
    }
    setProcessing(true);
    try {
      const arrBuf = rawArrayBufRef.current || (await rawBlob.arrayBuffer());
      const wavBlob = await applyFilterToArrayBuffer(arrBuf, newPreset);
      if (generation !== filterRef.current) return;
      setProcessedBlob(wavBlob);
      replacePreview(wavBlob);
    } catch (e) {
      if (generation !== filterRef.current) return;
      setPreset("none");
      logger.warn("voice-recorder", "filter apply failed", e);
      setProcessedBlob(rawBlob);
      replacePreview(rawBlob);
    } finally { if (generation === filterRef.current) setProcessing(false); }
  };

  // Pick an ambient bed (nature / mood) — hits iTunes search via /api/music/search
  // and auto-selects the first result. Cancellable mid-flight.
  const pickAmbientBed = async (bed) => {
    const session = sessionRef.current;
    setPickingBedId(bed.id);
    try {
      const { data } = await api.get(`/music/search?q=${encodeURIComponent(bed.query)}&limit=1`);
      if (session !== sessionRef.current) return;
      const track = Array.isArray(data?.tracks) ? data.tracks[0] : null;
      if (track?.url) {
        setMusic({
          ...track,
          // Tag with the bed so the UI can highlight which bed is active.
          bed_id: bed.id,
          // Override label so the chip reads "🌧 Rain" instead of the actual song title.
          title: `${bed.emoji} ${bed.label}`,
        });
      }
    } catch (err) {
      logger.warn("voice-recorder", "ambient bed fetch failed", err);
    } finally {
      if (session === sessionRef.current) setPickingBedId(null);
    }
  };

  const send = async () => {
    const out = processedBlob || rawBlob;
    if (!out || !onComplete || sendingRef.current || startingRef.current || processing) return;
    const session = sessionRef.current;
    sendingRef.current = true; setSending(true); setError(null);
    try {
      await onComplete({ blob: out, duration: seconds, filter: preset, mimeType: out.type || "audio/webm", music: music?.url ? { url: music.url, title: music.title, artist: music.artist } : null });
    } catch (_) {
      if (session === sessionRef.current) setError("Couldn't send. Your recording is still here—try again.");
    } finally {
      if (session === sessionRef.current) { sendingRef.current = false; setSending(false); }
    }
  };

  if (!open) return null;
  // Audio-reactive button scale: 1.0 at silence → 1.18 at peak.
  const reactiveScale = recording ? 1 + Math.min(level, 1) * 0.18 : 1;
  const reactiveGlow = recording ? `0 0 ${24 + level * 60}px hsl(var(--primary) / ${0.35 + level * 0.45})` : "none";

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-3xl bg-black/90 flex items-end sm:items-center justify-center" data-testid="voice-recorder">
      <div className="w-full max-w-md rounded-t-3xl sm:rounded-3xl border-t border-border sm:border bg-card relative noise overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-semibold">{title}</h3>
            {subtitle && (
              <p className="text-[11px] text-primary font-mono tracking-wider mt-0.5 truncate" data-testid="voice-recorder-subtitle">
                {subtitle}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters((v) => !v)} disabled={recording} data-testid="voice-filters-toggle"
              className={`p-2 rounded-full ${showFilters ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"} disabled:opacity-40`}>
              <SlidersHorizontal size={18} />
            </button>
            <button onClick={onClose} disabled={sending} aria-label="Close recorder" className="text-muted-foreground hover:text-foreground" data-testid="recorder-close">
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Ambient + custom music rail — only when NOT recording yet. */}
        {!recording && !rawBlob && (
          <div className="px-3 pt-3 pb-2 border-b border-border" data-testid="voice-bed-rail">
            <div className="flex items-center justify-between mb-2 px-1">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Background mood (optional)</p>
              {music && (
                <button onClick={() => setMusic(null)} data-testid="clear-bed" className="text-[10px] font-bold text-destructive uppercase tracking-wider">
                  Clear
                </button>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
              {AMBIENT_BEDS.map((b) => {
                const active = music?.bed_id === b.id;
                const loading = pickingBedId === b.id;
                return (
                  <button
                    key={b.id}
                    onClick={() => pickAmbientBed(b)}
                    disabled={loading}
                    data-testid={`bed-${b.id}`}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition active:scale-95 ${active ? "bg-primary text-primary-foreground border-primary glow" : "border-border text-foreground/80 hover:bg-secondary/60"} disabled:opacity-50`}
                  >
                    <span className="mr-1">{b.emoji}</span>{b.label}{loading && <span className="ml-1 opacity-60">…</span>}
                  </button>
                );
              })}
              <button
                onClick={() => setShowMusicPicker(true)}
                data-testid="bed-custom"
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition active:scale-95 ${music && !music.bed_id ? "bg-primary text-primary-foreground border-primary glow" : "border-border text-foreground/80 hover:bg-secondary/60"}`}
              >
                <MusicNotes size={12} weight="fill" className="inline mr-1" />
                {music && !music.bed_id ? music.title?.slice(0, 18) : "Pick song"}
              </button>
            </div>
          </div>
        )}

        {showFilters && (
          <div className="px-3 pt-3 pb-1 border-b border-border" data-testid="voice-filter-strip">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar">
              {VOICE_FILTERS.map((f) => (
                <button
                  type="button"
                  key={f.id}
                  onClick={() => applyFilter(f.id)}
                  disabled={recording || starting || sending || processing || !rawBlob}
                  data-testid={`voice-filter-${f.id}`}
                  className={`shrink-0 px-3 py-2 rounded-full text-xs font-semibold border ${preset === f.id ? "bg-primary text-primary-foreground border-primary glow" : "border-border text-foreground/80"} disabled:opacity-40 active:scale-95 transition`}
                >
                  <span className="mr-1">{f.short}</span>{f.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {!rawBlob ? "Record first, then tap a filter to apply" : processing ? "Applying filter…" : "Tap a filter to preview — one recording is enough"}
            </p>
          </div>
        )}

        <div className="p-6 flex flex-col items-center gap-5">
          {/* Audio-reactive mic ring */}
          <div
            className={`relative h-44 w-44 rounded-full flex items-center justify-center transition-transform duration-75`}
            style={{
              transform: `scale(${reactiveScale})`,
              boxShadow: reactiveGlow,
            }}
            data-testid="recorder-mic-ring"
          >
            {/* Outer ring — responds to level */}
            <div
              className="absolute inset-0 rounded-full border-2 border-primary/40 transition-all"
              style={{
                opacity: 0.35 + level * 0.6,
                transform: `scale(${1 + level * 0.05})`,
              }}
              aria-hidden
            />
            {/* Middle pulsing ring (only while recording) */}
            {recording && (
              <div className="absolute inset-2 rounded-full border border-primary/30 animate-ping" aria-hidden />
            )}
            <div className="relative h-32 w-32 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center backdrop-blur-md">
              <Microphone size={56} weight="fill" className="text-primary" />
            </div>
          </div>
          <div className="font-mono text-3xl tracking-widest tabular-nums" data-testid="recorder-timer">{fmt(seconds)}</div>
          {starting && <p role="status">Starting microphone…</p>}
          {error && <p role="alert" className="text-sm text-destructive text-center max-w-xs">{error}</p>}
          {previewUrl && (
            <audio src={previewUrl} controls className="w-full" data-testid="recorder-preview" />
          )}
          <div className="flex items-center gap-3 pt-1">
            {!recording && !rawBlob && (
              <button
                onClick={start}
                disabled={starting || sending}
                data-testid="recorder-start"
                aria-label="Start recording"
                className="h-16 w-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center glow active:scale-90 transition shadow-2xl"
              >
                <Microphone size={26} weight="fill" />
              </button>
            )}
            {recording && (
              <button
                onClick={stop}
                data-testid="recorder-stop"
                aria-label="Stop recording"
                className="h-16 w-16 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center active:scale-90 transition shadow-2xl"
              >
                <StopCircle size={30} weight="fill" />
              </button>
            )}
            {rawBlob && !recording && (
              <>
                <button onClick={start} disabled={starting || sending || processing} data-testid="recorder-retry" className="h-11 px-4 rounded-full border border-border text-sm font-semibold active:scale-95 transition">Re-record</button>
                <button onClick={send} disabled={processing || starting || sending} data-testid="recorder-send" className="h-11 px-5 rounded-full bg-primary text-primary-foreground flex items-center gap-2 font-semibold glow-hover disabled:opacity-50 active:scale-95 transition">
                  <PaperPlaneRight size={16} weight="fill" /> {sending ? "Sending…" : "Send"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <PermissionDeniedDialog
        denial={denial}
        onClose={() => setDenial(null)}
        onRetry={() => { setDenial(null); start(); }}
      />
      <MusicPicker
        open={showMusicPicker}
        onClose={() => setShowMusicPicker(false)}
        onPick={(t) => { setMusic(t); setShowMusicPicker(false); }}
        selectedId={music?.id}
      />
    </div>
  );
}
